/* eslint-disable jsx-a11y/role-supports-aria-props */
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  UseFormRegisterReturn,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useJsApiLoader } from '@react-google-maps/api';
import { createPortal } from 'react-dom';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../../utils/googleMapsLoader';

interface InputWithPlacesAPIProps {
  nameField: any;
  error?: string;
  placeholder?: string;
  defaultValue?: string;
  addressSelectedFromDropdown: boolean;
  onSelectAddress: (formattedAddress: string, components: any) => void;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  register: (name: keyof any, rules?: any) => UseFormRegisterReturn;
  setAddressSelectedFromDropdown: (value: boolean) => void;
}

const InputWithPlacesAPI: React.FC<InputWithPlacesAPIProps> = ({
  nameField,
  error,
  placeholder = '',
  defaultValue = '',
  addressSelectedFromDropdown,
  setAddressSelectedFromDropdown,
  onSelectAddress,
  watch,
  setValue,
  register,
}) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const [hasFocused, setHasFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [token, setToken] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const [open, setOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });

  const hasSelectedFromDropdownRef = useRef(false);

  useEffect(() => {
    if (addressSelectedFromDropdown) {
      hasSelectedFromDropdownRef.current = true;
    }
  }, [addressSelectedFromDropdown]);

  const { ref: registerRef } = register(nameField, {
    required: 'Address is required',
    validate: (value: string) => {
      // If empty, show normal required message
      if (!value || value.trim().length === 0) {
        return 'Address is required';
      }

      // If user typed something but never selected from dropdown:
      if (!hasSelectedFromDropdownRef.current) {
        return 'You must select an address from the dropdown';
      }

      return true;
    },
  });

  const recalc = () => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setDropdownRect({
      top: r.bottom + window.scrollY, // page coords
      left: r.left + window.scrollX,
      width: r.width,
    });
  };

  useLayoutEffect(() => {
    recalc();
  }, []);

  // Recalc on open, on suggestion changes, on resize/scroll (capture true to catch modal scrolls)
  useEffect(() => {
    if (!open) return;
    recalc();
    const onAnyScroll = () => recalc();
    const onResize = () => recalc();
    window.addEventListener('scroll', onAnyScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onAnyScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, suggestions]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (inputRef.current && !inputRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Open when we have suggestions
  useEffect(() => {
    setOpen(hasFocused && suggestions.length > 0);
  }, [hasFocused, suggestions]);

  useEffect(() => {
    const loadToken = async () => {
      if (isLoaded && !token) {
        const placesLib = (await window.google.maps.importLibrary(
          'places'
        )) as google.maps.PlacesLibrary;
        const newToken = new placesLib.AutocompleteSessionToken();
        setToken(newToken);
      }
    };
    loadToken();
  }, [defaultValue, isLoaded, token]);

  const watchedValue = watch(nameField);

  useEffect(() => {
    if (
      hasFocused &&
      typeof watchedValue === 'string' &&
      watchedValue.length > 2 &&
      isLoaded &&
      token
    ) {
      const fetchSuggestions = async () => {
        try {
          if (!window.google?.maps) return;

          const { AutocompleteSuggestion } = await (
            window.google.maps as any
          ).importLibrary('places');

          const request = {
            input: watchedValue || '',
            sessionToken: token,
            language: 'en-US',
            region: 'us',
          };

          const { suggestions } =
            await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

          setSuggestions(suggestions);
        } catch (error) {
          console.error('Error fetching autocomplete suggestions:', error);
          setSuggestions([]);
        }
      };
      fetchSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [token, isLoaded, hasFocused, watchedValue, nameField]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedInsideInput =
        inputRef.current && inputRef.current.contains(target);
      const clickedInsideDropdown =
        dropdownRef.current && dropdownRef.current.contains(target);

      if (!clickedInsideInput && !clickedInsideDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick, true); // capture helps prevent races
    return () => document.removeEventListener('mousedown', onDocClick, true);
  }, [open]);

  const handleSelect = async (suggestion: any) => {
    const placePrediction = suggestion.placePrediction;
    const place = placePrediction.toPlace();
    await place.fetchFields({
      fields: ['formattedAddress', 'addressComponents'],
    });
    const formattedAddress = place.formattedAddress;
    const addressComponents = place.addressComponents;

    hasSelectedFromDropdownRef.current = true;
    setAddressSelectedFromDropdown(true);

    setValue(nameField, formattedAddress, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setSuggestions([]);
    onSelectAddress(formattedAddress, addressComponents);
  };

  return (
    <>
      <div className="relative w-full">
        <input
          ref={(el) => {
            inputRef.current = el;
            registerRef(el);
          }}
          placeholder={placeholder}
          onChange={(e) => {
            setValue(nameField, e.target.value, {
              shouldDirty: true,
            });
            setHasFocused(true);
            hasSelectedFromDropdownRef.current = false;
            setAddressSelectedFromDropdown(false);
          }}
          onFocus={() => {
            setHasFocused(true);
            setOpen(true);
          }}
          className={`mt-1 block h-[38px] w-full border px-3 py-2 ${
            error ? 'border-red-500' : 'border-gray-300'
          } focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
          style={{ borderRadius: 0 }}
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      </div>

      {open &&
        createPortal(
          <ul
            ref={dropdownRef}
            role="listbox"
            style={{
              position: 'absolute',
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
              maxHeight: 320,
              overflowY: 'auto',
              zIndex: 5000,
            }}
            className="border border-gray-300 bg-white shadow-xl"
          >
            {suggestions.map((sugg, idx) => (
              <li
                key={idx}
                onMouseDown={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleSelect(sugg);
                  setAddressSelectedFromDropdown(true);
                  setHasFocused(false);
                  setOpen(false);
                }}
                className="cursor-pointer px-3 py-2 hover:bg-gray-100"
              >
                {sugg.placePrediction.text.toString()}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </>
  );
};

export default InputWithPlacesAPI;
